import { useAuth } from "@/contexts/AuthContext";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Clock,
  LogOut,
  User,
  UserRoundCog,
  X,
  TabletSmartphone,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  BarChart3,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { AppLogo } from "@/components/AppLogo";
import NotificationBell from "../notifications/NotificationBell";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex items-center rounded-lg transition-all duration-200",
      collapsed
        ? "justify-center gap-0 px-2 py-2.5 lg:px-2"
        : "gap-3 px-3 py-2.5",
      isActive
        ? "bg-white/20 text-white font-medium"
        : "text-white/80 hover:bg-white/10 hover:text-white"
    );

  return (
    <aside
      className={cn(
        "flex h-full flex-shrink-0 flex-col border-r border-primary/20 bg-primary transition-all duration-300",
        "relative",
        "fixed left-0 z-50 lg:static",
        "transform transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        isOpen ? "top-0 h-screen" : "top-16 h-[calc(100vh-4rem)]",
        "lg:top-0 lg:h-full",
        "w-64",
        collapsed ? "lg:w-16" : "lg:w-64"
      )}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-lg p-2 text-white hover:bg-white/20 lg:hidden"
        aria-label="Fermer le menu"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Logo */}
      <div
        className={cn(
          "flex border-b border-white/20 px-4 py-3",
          collapsed && "lg:justify-center lg:px-2 lg:py-2"
        )}
      >
        <div
          className={cn(
            "flex w-full flex-col items-center gap-1.5 text-center",
            collapsed && "lg:w-auto"
          )}
        >
          <div
            className={cn(
              "flex w-full items-center justify-center gap-2",
              collapsed && "lg:justify-center"
            )}
          >
            <AppLogo size="md" className="h-10 w-10 shrink-0" />
            <h1
              className={cn(
                "font-display text-2xl font-normal leading-tight text-white",
                collapsed && "lg:hidden"
              )}
            >
              Pointo
            </h1>
          </div>
          <p
            className={cn(
              "text-xs text-white/70",
              collapsed && "lg:hidden"
            )}
          >
            Espace administrateur
          </p>
        </div>
      </div>

      {/* Infos utilisateur */}
      <div
        className={cn(
          "border-b border-white/20 p-4",
          collapsed && "lg:p-2"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-3 rounded-lg bg-white/10 p-3",
            collapsed && "lg:justify-center lg:p-2"
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20">
            <User className="h-4 w-4 text-white" />
          </div>
          <div
            className={cn("min-w-0 flex-1", collapsed && "lg:hidden")}
          >
            {user?.company && (
              <p className="truncate text-xs text-white/60">
                {user.company.name}
              </p>
            )}
            <p className="truncate text-sm font-medium text-white">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.firstName || user?.lastName || "Utilisateur"}
            </p>
            <p className="text-xs text-white/70">{user?.role}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
        {user?.role === "SUPERUSER" && (
          <>
            {/* 1. Vue d'ensemble */}
            <NavLink to="/superuser/dashboard" className={navLinkClass} onClick={handleLinkClick} title="Dashboard">
              <LayoutDashboard className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Dashboard</span>
            </NavLink>
            {/* 2. Gestion principale */}
            <NavLink to="/superuser/companies" className={navLinkClass} onClick={handleLinkClick} title="Entreprises">
              <Building2 className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Entreprises</span>
            </NavLink>
            {/* 3. Monitoring quotidien */}
            <NavLink to="/sessions" className={navLinkClass} onClick={handleLinkClick} title="Sessions">
              <Clock className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Sessions</span>
            </NavLink>
            {/* 4. Organisation hebdomadaire */}
            <NavLink to="/admin/planning" className={navLinkClass} onClick={handleLinkClick} title="Planning">
              <CalendarDays className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Planning</span>
            </NavLink>
            {/* 5. Gestion ponctuelle */}
            <NavLink to="/superuser/users/employees" className={navLinkClass} onClick={handleLinkClick} title="Employés">
              <Users className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Employés</span>
            </NavLink>
            {/* 6. Administration rare */}
            <NavLink to="/superuser/users/admins" className={navLinkClass} onClick={handleLinkClick} title="Comptes">
              <UserRoundCog className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Comptes</span>
            </NavLink>
          </>
        )}


        {user?.role === "ADMIN" && (
          <>
            {/* 1. Vue d'ensemble */}
            <NavLink to="/admin/dashboard" className={navLinkClass} onClick={handleLinkClick} title="Dashboard">
              <LayoutDashboard className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Dashboard</span>
            </NavLink>
            {/* 2. Monitoring quotidien */}
            <NavLink to="/sessions" className={navLinkClass} onClick={handleLinkClick} title="Sessions">
              <Clock className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Sessions</span>
            </NavLink>
            {/* 3. Organisation hebdomadaire */}
            <NavLink to="/admin/planning" className={navLinkClass} onClick={handleLinkClick} title="Planning">
              <CalendarDays className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Planning</span>
            </NavLink>
            {/* 4. Gestion ponctuelle */}
            <NavLink to="/admin/employees" className={navLinkClass} onClick={handleLinkClick} title="Employés">
              <Users className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Employés</span>
            </NavLink>
            {/* 5. Analyse mensuelle */}
            <NavLink to="/admin/reports" className={navLinkClass} onClick={handleLinkClick} title="Rapports">
              <BarChart3 className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Rapports</span>
            </NavLink>
            {/* 6. Configuration rare */}
            <NavLink to="/admin/my-company" className={navLinkClass} onClick={handleLinkClick} title="Ma Pointeuse">
              <TabletSmartphone className="h-5 w-5 shrink-0" />
              <span className={cn(collapsed && "lg:hidden")}>Ma Pointeuse</span>
            </NavLink>
            
          </>
        )}
      </nav>

      <div className="border-t border-white/20 p-2">
        <NotificationBell collapsed={collapsed} />
      </div>

      <div className="border-t border-white/20 p-2">
        <button
          type="button"
          onClick={logout}
          title="Déconnexion"
          className={cn(
            "flex w-full items-center rounded-lg px-3 py-2.5 text-white/90 transition-colors duration-200 hover:bg-white/10",
            collapsed ? "justify-center gap-0 lg:px-2" : "gap-3"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          <span className={cn(collapsed && "lg:hidden")}>Déconnexion</span>
        </button>
      </div>

      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="absolute -right-3 top-24 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-card shadow-sm transition-colors hover:bg-secondary lg:flex"
        aria-label={collapsed ? "Développer le menu" : "Réduire le menu"}
      >
        {collapsed ? (
          <ChevronRight className="h-3 w-3 text-foreground" />
        ) : (
          <ChevronLeft className="h-3 w-3 text-foreground" />
        )}
      </button>

    </aside>
  );
};
